import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ServerCog } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { MpesaCredentials } from '../components/MpesaCredentials';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import type { DeveloperSettings } from '../lib/types';

/**
 * The three switches, typed.
 *
 * A tuple array here made the key a plain `string`, which cannot index the
 * settings object and cannot be a computed property. Naming the keys is both
 * shorter to read and the thing that makes the loop check.
 */
const TOGGLES: Array<{
  key: 'showDiagnostics' | 'showErrorReferences' | 'allowDemoData';
  title: string;
  why: string;
}> = [
  {
    key: 'showDiagnostics',
    title: 'Show the technical panel on System status',
    why: 'Queue depths, provider reachability, build version, migration state.',
  },
  {
    key: 'showErrorReferences',
    title: 'Show the reference on every failure',
    why: 'The code somebody quotes when they report a problem. Little reason to hide it.',
  },
];

/** Seconds as something readable. Uptime is the one figure people quote. */
const forHumans = (seconds: number): string => {
  if (seconds < 90) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hours`;
  return `${Math.round(hours / 24)} days`;
};

/**
 * Developer options.
 *
 * The part of Settings addressed to whoever maintains this system rather than
 * whoever runs the business. Separated for one reason: an admin looking for
 * business hours should not scroll past a log level, and somebody debugging at
 * eight in the evening should not hunt through opening times for the switch they
 * need.
 *
 * The facts at the top are read-only and shallow on purpose — the mode, the
 * version, which providers really send. Enough to answer "what is this
 * installation actually doing" without becoming a second configuration screen
 * that can disagree with the first.
 */
export function DeveloperPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const overview = useQuery({ queryKey: ['developer'], queryFn: api.developerOverview });

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries();
  };

  const save = useMutation({
    mutationFn: (patch: Partial<DeveloperSettings>) =>
      api.updateSettings({
        developer: { ...(overview.data?.developer as DeveloperSettings), ...patch },
      } as never),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });




  const data = overview.data;

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Settings', to: '/settings' }, { label: 'Developer options' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Developer options
          <HelpHint>
            Settings for whoever maintains this system. Everything here needs the same permission
            as the rest of Settings, and every change is written to the audit log.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          What this installation is, how much it writes to its log, and whether it is holding any
          invented data.
        </p>
      </div>

      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      {overview.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Reading the environment
        </p>
      )}

      {data && (
        <>
          {/* ── What this is ─────────────────────────────────────────────── */}
          <section className="card">
            <h2 className="flex items-center gap-2 font-heading text-lg">
              <ServerCog className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              This installation
            </h2>

            {/*
              The database *host*, never its URL. A connection string carries a
              password, and this screen is one screenshot away from a support
              thread.
            */}
            <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {[
                ['Mode', data.environment.mode],
                ['Organisation', data.environment.organisation],
                ['Console', data.environment.appUrl],
                ['API', data.environment.apiUrl],
                ['Database host', data.environment.databaseHost],
                ['Node', data.environment.node],
                ['Running for', forHumans(data.environment.uptimeSeconds)],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3 border-b border-border py-1">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="break-all text-right font-medium">{value}</dd>
                </div>
              ))}
            </dl>

            <p className="mt-3 text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
              Channels
            </p>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {Object.entries(data.providers).map(([channel, provider]) => (
                <span
                  key={channel}
                  className={`badge ${
                    provider === 'mock'
                      ? 'bg-accent-50 text-accent-700'
                      : 'bg-navy-50 text-navy-700'
                  }`}
                >
                  {channel}: {provider}
                  {provider === 'mock' && ' — nothing is really sent'}
                </span>
              ))}
            </div>
          </section>

          {/* ── Options ──────────────────────────────────────────────────── */}
          <section className="card space-y-4">
            <h2 className="font-heading text-lg">Options</h2>

            <label className="block text-sm">
              How much the API writes to its log
              <select
                className="input mt-1"
                value={data.developer.logLevel}
                disabled={save.isPending}
                onChange={(event) =>
                  save.mutate({ logLevel: event.target.value as typeof data.developer.logLevel })
                }
              >
                <option value="error">Errors only</option>
                <option value="warn">Errors and warnings</option>
                <option value="log">Normal</option>
                <option value="debug">Everything — for finding a problem</option>
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                “Everything” logs each refused request too. Useful while something is wrong and
                expensive when nothing is, so turn it back down afterwards.
              </span>
            </label>

            {TOGGLES.map(({ key, title, why }) => (
              <label key={key} className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={Boolean(data.developer[key])}
                  disabled={save.isPending}
                  onChange={(event) => save.mutate({ [key]: event.target.checked })}
                />
                <span>
                  <span className="font-medium">{title}</span>
                  <span className="block text-xs text-muted-foreground">{why}</span>
                </span>
              </label>
            ))}

            {save.isPending && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Saving
              </p>
            )}
          </section>

          {/*
            Demonstration data is not offered here any more.

            It was a way to fill an empty system for a demonstration, and this
            is not an empty system — it carries real customers, real invoices
            and real money. A control whose only use has passed is a control
            that will one day be pressed by mistake, and "invented customers in
            a live console are how somebody rings a number that does not exist".

            The endpoints stay: loading has always been refused on production,
            and removal must remain available for any system that still has a
            batch in it. Only the buttons are gone.
          */}

          {/*
            The Daraja credentials.

            Here rather than on the main Settings page because that is where
            they were asked for, and because the pairing is defensible: this is
            an integration credential with a sandbox switch beside it, which is
            closer to a log level than to opening hours. The paybill number
            itself is a business fact, but it is no use without the three
            things next to it, and splitting them across two screens would mean
            configuring M-Pesa in two places.
          */}
          <MpesaCredentials />
        </>
      )}
    </div>
  );
}
